import * as React from 'react';
import { Window } from '@progress/kendo-react-dialogs';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SetValueOnObject } from '../../utils/UnsafeTypescriptUtil';
import { SubmittedKitRun, blankSubmittedKitRun, apiModelToSubmittedKitRun } from '../../models/SubmittedKitRun';
import { protectedResources } from '../../authConfig';
import { useFetchWithMsal } from '../../hooks/useFetchWithMsal';
import { LoadPage } from '../../components/LoadPage';
import { KitRunStatus, kitRunStatuses } from '../../models/kitRunStatus';
import { CommentListAndButton } from '../Comments/Comment_List';
import { ObjectTypes } from '../../models/comments';
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import { AddComment } from '../Comments/AddCommentBox';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Input } from '@progress/kendo-react-inputs';

interface ManageSubmittedKitRunProps {
    submittedKitId?: number,
    closeWindow?: Function,
    children?: React.ReactNode
}

const ManageSubmittedKitRun = (props: ManageSubmittedKitRunProps) => {
    const params = useParams();
    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [commentKey, setCommentKey] = useState(Date.now());
    const getincludeOrExcludeString = () => {
        return submittedKitRun.KitRunStatusId === KitRunStatus.Included ?
            'Exclude' :
            'Include';
    }

    const getFreshSubmittedKitRun = () => {
        let _submittedKitRun = structuredClone(blankSubmittedKitRun);
        if (params.SubmittedKitRunId !== undefined) {
            _submittedKitRun = SetValueOnObject(_submittedKitRun, "Id", params.SubmittedKitRunId) as SubmittedKitRun;
        }
        else if (props.submittedKitId) {
            _submittedKitRun = SetValueOnObject(_submittedKitRun, "SubmittedKitId", props.submittedKitId) as SubmittedKitRun;
        }
        return _submittedKitRun;
    }

    const onChange_SubmittedKitRun = (e: any, propName: string) => {
        let updatedSubmittedKitRun: SubmittedKitRun = SetValueOnObject(submittedKitRun, propName, e.target.value) as SubmittedKitRun;
        setSubmittedKitRun(updatedSubmittedKitRun);
    }

    const submitKitRun = (formRenderProps: FormRenderProps) => {
        if (!formRenderProps.valid || submittedKitRun.Id > 0) {
            return;
        }

        execute('POST', protectedResources.apiSubmittedKitRun.endpoint + "CreateSubmittedKitRun", submittedKitRun)
            .then(
                ((result: any) => {
                    if (result?.id) {
                        setSubmittedKitRun(getFreshSubmittedKitRun());
                        props.closeWindow?.();
                    }
                }),
            );
    };

    const [submittedKitRun, setSubmittedKitRun] = useState(getFreshSubmittedKitRun());

    const afterRerunKit = (result: any) => {
        if (result?.id) {
            setSubmittedKitRun({ ...submittedKitRun, KitRunStatusId: submittedKitRun.KitRunStatusId === KitRunStatus.Included ? KitRunStatus.ManuallyExcluded : KitRunStatus.Included });
            setCommentKey(Date.now());
            setShowCommentDialog(false);
        }
    };

    const getSubmittedKitRun = () => {
        if (submittedKitRun.Id > 0) {
            return execute('GET', protectedResources.apiSubmittedKitRun.endpoint + "GetSubmittedKitRun/?Id=" + submittedKitRun.Id)
                .then(
                    ((result: any) => {
                        if (result) { setSubmittedKitRun(apiModelToSubmittedKitRun(result)); }
                    }),
                );
        }
        return null;
    }

    React.useEffect(() => {
        getSubmittedKitRun();
    }, [execute]);

    return <LoadPage isLoading={isLoading}>
        {showCommentDialog &&
            <Window title={getincludeOrExcludeString() + ' Run'} onClose={() => { setShowCommentDialog(false); }} initialHeight={300} initialWidth={400}>
                <AddComment loginPrompt={true}
                    objectId={submittedKitRun.Id}
                    buttonText={getincludeOrExcludeString() + " Run"} header={"Enter the reason to " + getincludeOrExcludeString() + " this run"}
                    endpoint={protectedResources.apiSubmittedKitRun.endpoint + getincludeOrExcludeString() + "SubmittedKitRun"} afterAddFunction={(result: any) => { afterRerunKit(result); }} execute={execute}></AddComment>
            </Window>}
        <GridLayout gap={{ cols: 15, rows: 15 }}>
            <GridLayoutItem row={1} col={1}>
                <Form initialValues={submittedKitRun} key={JSON.stringify(submittedKitRun)} render={(formRenderProps) =>
                    <FormElement style={{ maxWidth: 650 }}>
                        <fieldset className={'k-form-fieldset'}>
                            <legend className={'k-form-legend'}>Please fill in the fields:</legend>

                            {submittedKitRun.Id > 0 &&
                                <div className="row">
                                    <div className="mb-3 col">
                                        Kit Barcode: <br></br>
                                        <Link to={"/submitted-kit-manage/" + submittedKitRun.SubmittedKitId}>
                                            {submittedKitRun.SubmittedKit?.Barcode}
                                        </Link>
                                    </div>
                                    <div className="mb-3 col">
                                        Status: <br></br>{kitRunStatuses[submittedKitRun.KitRunStatusId].name}
                                    </div>
                                </div>}
                            {submittedKitRun.Id > 0 ? <>
                                <div className="row">
                                    <div className="mb-3 col">
                                        <span className="column-item">Run Date:</span>
                                        &emsp;{submittedKitRun.RunDate.toLocaleDateString()}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="mb-3 col">
                                        <span className="column-item">Measurement:</span>
                                        &emsp;{submittedKitRun.Measurement}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="mb-3 col">
                                        <span className="column-item">Pipet Volume:</span>
                                        &emsp;{submittedKitRun.PipetVolume}
                                    </div>
                                </div>
                            </> :
                                <>
                                    <div className="row">
                                        <div className="mb-3 col">
                                            <Field name={'RunDate'} component={DatePicker} label={'RunDate'} required={false} onChange={(event) => onChange_SubmittedKitRun(event, "RunDate")} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="mb-3 col">
                                            <Field name={'Measurement'} component={Input} label={'Measurement'} required={false} onChange={(event) => onChange_SubmittedKitRun(event, "Measurement")} />
                                        </div>
                                    </div>
                                </>}
                        </fieldset>
                        <div className="k-form-buttons">
                            {submittedKitRun.Id > 0 ?
                                <Button
                                    onClick={() => setShowCommentDialog(true)}
                                    type={'submit'}
                                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                    {getincludeOrExcludeString()}
                                </Button> :
                                <Button
                                    onClick={() => submitKitRun(formRenderProps)}
                                    type={'submit'}>
                                    Submit
                                </Button>}
                        </div>
                    </FormElement>} />
            </GridLayoutItem>
            {submittedKitRun.Id > 0 &&
                <GridLayoutItem row={1} col={2}>
                    <CommentListAndButton
                        key={commentKey}
                        objectId={submittedKitRun.Id}
                        objectType={ObjectTypes.SubmittedKitRun}>
                    </CommentListAndButton>
                </GridLayoutItem>}
        </GridLayout>
    </LoadPage >;
};

export default ManageSubmittedKitRun;